import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, FilePlus2, FileSignature, Heart, Inbox, Lock, Plus, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errMsg } from "@/lib/api";

export function GlobalSearch() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    const key = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) {
      setRes([]);
      return;
    }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const go = (link) => {
    setOpen(false);
    setQ("");
    nav(link);
  };
  return (
    <>
      <button onClick={() => setOpen(true)} title="Cerca (Ctrl+K)" className="p-2 rounded-full text-[#47606B] hover:bg-[#EAF5F7]" data-testid="global-search-button"><Search size={17} /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Cerca</DialogTitle>
          <div className="flex items-center gap-3 px-4 border-b border-[#D4E6EC]"><Search size={17} className="text-[#7B929C]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, telefono, contratto, preventivo…" className="w-full py-4 text-sm focus:outline-none" data-testid="global-search-input" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {q.trim().length >= 2 && res.length === 0 && <div className="text-sm text-[#7B929C] p-4" data-testid="global-search-empty">Nessun risultato.</div>}
            {res.map((r, i) => (
              <button key={i} onClick={() => go(r.link)} className="w-full text-left rounded-lg px-3 py-2.5 hover:bg-[#EAF5F7] flex items-center gap-3" data-testid={`global-search-result-${i}`}>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EAF5F7] text-[#0B6E8F] w-24 text-center shrink-0">{r.type}</span>
                <span className="min-w-0"><span className="block text-sm font-medium truncate">{r.title}</span><span className="block text-xs text-[#7B929C] truncate">{r.sub}</span></span>
              </button>
            ))}
            {!q && <div className="text-xs text-[#7B929C] p-4">Suggerimento: premi Ctrl+K da qualsiasi pagina.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function QuickCreate() {
  const nav = useNavigate();
  const create = (url, base) => async () => {
    try {
      const r = await api.post(url);
      nav(`${base}/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const items = [
    [FilePlus2, "Nuovo preventivo", create("/quotes", "/preventivi"), "quote"],
    [FileSignature, "Nuovo contratto", create("/contracts", "/contratti"), "contract"],
    [Inbox, "Nuova richiesta", () => nav("/richieste?new=1"), "lead"],
    [CalendarPlus, "Appuntamento", () => nav("/calendario?new=appt"), "appt"],
    [Lock, "Opzione su una data", () => nav("/calendario?new=hold"), "hold"],
    [Heart, "Nuovo cliente", () => nav("/clienti?new=1"), "client"],
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button title="Crea" className="h-9 w-9 rounded-full bg-[#34B3B0] text-white flex items-center justify-center hover:bg-[#2A9D8F] transition-colors mx-1" data-testid="quick-create-button"><Plus size={18} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {items.map(([Icon, label, fn, tid]) => <DropdownMenuItem key={tid} onClick={fn} className="gap-2 cursor-pointer" data-testid={`quick-create-${tid}`}><Icon size={15} /> {label}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
